// src/pages/PredictDisruption.js
// AgentBlayna Calendar — modern, calm, AI-paced execution (Full file, paste-safe)

import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Grid,
  Paper,
  Typography,
  Stack,
  Button,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  useMediaQuery,
} from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import { useOutletContext } from "react-router-dom";

import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";

import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import CheckCircleOutlineRoundedIcon from "@mui/icons-material/CheckCircleOutlineRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import EventAvailableRoundedIcon from "@mui/icons-material/EventAvailableRounded";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";

import { apiFetch } from "../api/http";
import MobileCalendarView from "../components/MobileCalendarView";

/* ------------------------------------------------------------------ */
/* Tunables */
/* ------------------------------------------------------------------ */

const DEFAULT_CALLS_PER_DAY = 3;
const UPCOMING_DAYS = 7;
const MAX_UPCOMING_RENDER = 10;

/* ------------------------------------------------------------------ */
/* UI Card */
/* ------------------------------------------------------------------ */

const Card = ({ children, sx }) => {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        borderRadius: 2,
        ...sx,
      }}
    >
      {children}
    </Paper>
  );
};

/* ------------------------------------------------------------------ */
/* Helpers */
/* ------------------------------------------------------------------ */

const startOfDay = (d) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
};

const addDays = (d, n) => {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
};

const dateKey = (d) => startOfDay(d).toISOString().slice(0, 10);

const severityRank = (sev) => {
  if (sev === "high") return 3;
  if (sev === "medium") return 2;
  return 1;
};

const safeDateFromAlert = (a) => {
  if (typeof a?.days_until === "number") {
    return addDays(new Date(), a.days_until);
  }
  if (a?.created_at) {
    const t = new Date(a.created_at);
    return Number.isNaN(t.getTime()) ? new Date() : t;
  }
  return new Date();
};

const isPriorityCall = (a) => {
  const s = String(a?.summary || "").toLowerCase();
  return a?.source_type === "follow_up" && s.startsWith("call ");
};

const isDealEvent = (a) => a?.source_type === "deal_event";
const isBirthday = (a) => a?.source_type === "birthday";
const isAnniversary = (a) => a?.source_type === "anniversary";

function paceCalls(calls, callsPerDay) {
  const today = startOfDay(new Date());

  const sorted = calls.slice().sort((a, b) => {
    const sr = severityRank(b.severity) - severityRank(a.severity);
    if (sr !== 0) return sr;
    return safeDateFromAlert(a).getTime() - safeDateFromAlert(b).getTime();
  });

  const scheduled = [];
  for (let i = 0; i < sorted.length; i++) {
    const dayOffset = Math.floor(i / Math.max(1, callsPerDay));
    scheduled.push({ ...sorted[i], _scheduledDay: addDays(today, dayOffset) });
  }
  return scheduled;
}

/* ------------------------------------------------------------------ */
/* FullCalendar modern styling injection (no external file needed) */
/* ------------------------------------------------------------------ */

function ModernCalendarCSS() {
  // Inject once per mount
  useEffect(() => {
    const id = "agentblayna-modern-fc-css";
    if (document.getElementById(id)) return;

    const style = document.createElement("style");
    style.id = id;
    style.innerHTML = `
      /* Base */
      .fc {
        font-family: Inter, system-ui, -apple-system, Segoe UI, Roboto, sans-serif;
        color: #0f172a;
      }

      /* Toolbar title */
      .fc .fc-toolbar-title {
        font-size: 22px;
        font-weight: 900;
        letter-spacing: -0.02em;
      }

      /* Buttons */
      .fc .fc-button {
        background: #f8fafc;
        border: 1px solid #e5e7eb;
        color: #0f172a;
        border-radius: 12px;
        padding: 7px 12px;
        font-weight: 700;
        box-shadow: 0 6px 18px rgba(15,23,42,0.06);
      }
      .fc .fc-button:hover {
        background: #eef2ff;
        border-color: #c7d2fe;
      }
      .fc .fc-button:disabled {
        opacity: 0.6;
      }
      .fc .fc-button-primary:not(:disabled).fc-button-active,
      .fc .fc-button-primary:not(:disabled):active {
        background: #2563eb !important;
        border-color: #2563eb !important;
        color: #fff !important;
      }

      /* Remove harsh grid borders */
      .fc-theme-standard td,
      .fc-theme-standard th {
        border: none !important;
      }

      /* Column headers */
      .fc .fc-col-header-cell-cushion {
        padding: 10px 0;
        font-size: 12px;
        font-weight: 900;
        text-transform: uppercase;
        color: #64748b;
        letter-spacing: 0.06em;
      }

      /* Time grid subtle lines */
      .fc .fc-timegrid-slot {
        border-top: 1px solid #f1f5f9 !important;
      }

      /* Now indicator subtle */
      .fc .fc-timegrid-now-indicator-line {
        border-color: rgba(239, 68, 68, 0.45) !important;
      }

      /* Day background hover */
      .fc .fc-daygrid-day-frame,
      .fc .fc-timegrid-col-frame {
        border-radius: 14px;
        transition: background 160ms ease;
      }
      .fc .fc-daygrid-day-frame:hover,
      .fc .fc-timegrid-col-frame:hover {
        background: rgba(37, 99, 235, 0.03);
      }

      /* Events: make hover feel modern */
      .fc .fc-event {
        border: none !important;
        background: transparent !important;
      }
      .fc .fc-event:hover {
        transform: translateY(-2px);
      }

      /* Remove default dot */
      .fc .fc-daygrid-event-dot {
        display: none !important;
      }

      /* Make all-day row calmer */
      .fc .fc-timegrid-axis-cushion,
      .fc .fc-timegrid-slot-label-cushion {
        color: #64748b;
        font-weight: 700;
      }
    `;
    document.head.appendChild(style);

    return () => {
      // keep style (don’t remove) to avoid flicker if user navigates back
    };
  }, []);

  return null;
}

/* ------------------------------------------------------------------ */
/* Color tokens (module scope so MobileCalendarView can receive them) */
/* ------------------------------------------------------------------ */

const priorityColor = {
  dot: "#FB7185",
  border: "#FECDD3",
  bg: "linear-gradient(135deg, #FFF1F2 0%, #FFE4E6 100%)",
  text: "#9F1239",
  shadow: "0 14px 38px rgba(251,113,133,0.22)",
  hoverShadow: "0 22px 50px rgba(251,113,133,0.32)",
};

const dealColor = {
  dot: "#2563EB",
  border: "#C7D2FE",
  bg: "linear-gradient(135deg, #EEF2FF 0%, #E0E7FF 100%)",
  text: "#1E3A8A",
  shadow: "0 14px 38px rgba(37,99,235,0.14)",
  hoverShadow: "0 22px 50px rgba(37,99,235,0.22)",
};

const personalColor = {
  dot: "#7C3AED",
  border: "#E9D5FF",
  bg: "linear-gradient(135deg, #FAF5FF 0%, #F3E8FF 100%)",
  text: "#5B21B6",
  shadow: "0 14px 38px rgba(124,58,237,0.12)",
  hoverShadow: "0 22px 50px rgba(124,58,237,0.20)",
};

const neutralColor = {
  dot: "#10B981",
  border: "#BBF7D0",
  bg: "linear-gradient(135deg, #ECFDF5 0%, #D1FAE5 100%)",
  text: "#065F46",
  shadow: "0 14px 38px rgba(16,185,129,0.10)",
  hoverShadow: "0 22px 50px rgba(16,185,129,0.18)",
};

/* ------------------------------------------------------------------ */
/* Main Component */
/* ------------------------------------------------------------------ */

export default function DealsAndPipeline() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const calendarRef = useRef(null);

  const outletCtx = useOutletContext() || {};
  const { onAiStatusChange, aiRefreshToken } = outletCtx;

  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(false);

  // pacing
  const [callsPerDay, setCallsPerDay] = useState(DEFAULT_CALLS_PER_DAY);

  // add dialog (local events v1)
  const [addOpen, setAddOpen] = useState(false);
  const [addType, setAddType] = useState("task"); // task | reminder | event
  const [addTitle, setAddTitle] = useState("");
  const [addDate, setAddDate] = useState(dateKey(new Date()));
  const [manualEvents, setManualEvents] = useState([]);

  // detail modal for a day
  const [dayOpen, setDayOpen] = useState(false);
  const [dayLabel, setDayLabel] = useState("");
  const [dayItems, setDayItems] = useState([]);

  /* ---------------- Load alerts ---------------- */

  const loadAlerts = async () => {
    try {
      setLoading(true);
      onAiStatusChange?.({ loading: true });

      const res = await apiFetch("/api/alerts");
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setAlerts(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error("Calendar load error:", e);
      setAlerts([]);
    } finally {
      setLoading(false);
      onAiStatusChange?.({
        loading: false,
        lastUpdated: new Date().toISOString(),
      });
    }
  };

  useEffect(() => {
    loadAlerts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aiRefreshToken]);

  /* ---------------- Build model ---------------- */

  const model = useMemo(() => {
    const calls = alerts.filter(isPriorityCall);
    const nonCalls = alerts.filter((a) => !isPriorityCall(a));

    const paced = paceCalls(calls, callsPerDay);

    // bucket calls by scheduled day
    const callsByDay = new Map(); // yyyy-mm-dd -> array
    paced.forEach((a) => {
      const key = dateKey(a._scheduledDay);
      const list = callsByDay.get(key) || [];
      list.push(a);
      callsByDay.set(key, list);
    });

    // build fc events
    const events = [];

    // grouped priority call bubble per day
    for (const [key, list] of callsByDay.entries()) {
      events.push({
        id: `calls_${key}`,
        title: "Priority Calls",
        start: new Date(key + "T00:00:00"),
        allDay: true,
        extendedProps: {
          kind: "calls_group",
          dateKey: key,
          calls: list,
        },
      });
    }

    // other alerts as individual all-day bubbles
    nonCalls.forEach((a) => {
      const d = startOfDay(safeDateFromAlert(a));
      events.push({
        id: `a_${a.id}`,
        title: a.summary || a.type || "Alert",
        start: d,
        allDay: true,
        extendedProps: {
          kind: isDealEvent(a)
            ? "deal"
            : isBirthday(a) || isAnniversary(a)
            ? "personal"
            : "other",
          raw: a,
        },
      });
    });

    // manual events (local)
    manualEvents.forEach((m) => {
      events.push({
        id: `m_${m.id}`,
        title: m.title,
        start: new Date(m.dateKey + "T00:00:00"),
        allDay: true,
        extendedProps: { kind: "manual", manual: m },
      });
    });

    // upcoming list (next 7 days) from buckets + alerts
    const today = startOfDay(new Date());
    const end = addDays(today, UPCOMING_DAYS);

    const upcoming = [];

    for (const [key, list] of callsByDay.entries()) {
      const day = new Date(key + "T00:00:00");
      if (day < today || day > end) continue;
      list.forEach((a) => upcoming.push({ type: "call", day, a }));
    }

    nonCalls.forEach((a) => {
      const day = startOfDay(safeDateFromAlert(a));
      if (day < today || day > end) return;
      upcoming.push({ type: "other", day, a });
    });

    manualEvents.forEach((m) => {
      const day = new Date(m.dateKey + "T00:00:00");
      if (day < today || day > end) return;
      upcoming.push({ type: "manual", day, m });
    });

    upcoming.sort((x, y) => {
      const dx = x.day.getTime() - y.day.getTime();
      if (dx !== 0) return dx;
      const sx = x.a ? severityRank(x.a.severity) : 0;
      const sy = y.a ? severityRank(y.a.severity) : 0;
      return sy - sx;
    });

    // top focus = top 3 most urgent calls or deal events
    const topFocus = alerts
      .slice()
      .sort((a, b) => severityRank(b.severity) - severityRank(a.severity))
      .filter((a) => isPriorityCall(a) || isDealEvent(a))
      .slice(0, 3);

    return {
      events,
      upcoming,
      topFocus,
      counts: {
        callsQueued: calls.length,
        upcomingEvents: alerts.filter(isDealEvent).length,
        relationshipTouches: alerts.filter((a) => isBirthday(a) || isAnniversary(a)).length,
      },
      callsByDay,
    };
  }, [alerts, manualEvents, callsPerDay]);

  /* ---------------- Actions ---------------- */

  const dismissAlert = async (id) => {
    try {
      await apiFetch(`/api/alerts/${id}/dismiss`, { method: "POST" });
      setAlerts((prev) => prev.filter((a) => a.id !== id));
    } catch (e) {
      console.error("Dismiss failed", e);
    }
  };

  const openAdd = () => {
    setAddTitle("");
    setAddType("task");
    setAddDate(dateKey(new Date()));
    setAddOpen(true);
  };

  const createManualEvent = () => {
    const title = addTitle.trim();
    if (!title) return;
    const item = {
      id: String(Date.now()),
      title,
      type: addType,
      dateKey: addDate,
      created_at: new Date().toISOString(),
    };
    setManualEvents((prev) => [item, ...prev]);
    setAddOpen(false);
  };

  const openDay = (key) => {
    // Build items for this day: calls group expanded + other alerts + manual
    const day = new Date(key + "T00:00:00");
    const label = day.toLocaleDateString(undefined, {
      weekday: "long",
      month: "short",
      day: "numeric",
    });

    const items = [];

    const calls = model.callsByDay.get(key) || [];
    calls.forEach((a) => items.push({ kind: "call", a }));

    // include other alerts that fall on this day naturally
    alerts.forEach((a) => {
      if (isPriorityCall(a)) return;
      const d = startOfDay(safeDateFromAlert(a));
      if (dateKey(d) !== key) return;
      items.push({ kind: "alert", a });
    });

    // include manual
    manualEvents.forEach((m) => {
      if (m.dateKey === key) items.push({ kind: "manual", m });
    });

    setDayLabel(label);
    setDayItems(items);
    setDayOpen(true);
  };

  /* ---------------- Bubble styles ---------------- */

  const bubbleSx = (c) => ({
    width: "100%",
    px: 1.2,
    py: 0.65,
    borderRadius: 3,
    background: c.bg,
    border: `1px solid ${c.border}`,
    color: c.text,
    boxShadow: c.shadow,
    fontWeight: 900,
    fontSize: 12.5,
    lineHeight: 1.15,
    display: "flex",
    alignItems: "center",
    gap: 1,
    transition: "transform 160ms ease, box-shadow 160ms ease",
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: c.hoverShadow,
    },
  });

  /* ---------------- Render ---------------- */

  return (
    <Box>
      <ModernCalendarCSS />

      {/* Header bar (product-like) */}
      <Card sx={{ mb: 2.5 }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1.25}
          alignItems={{ xs: "stretch", sm: "center" }}
          justifyContent="space-between"
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <EventAvailableRoundedIcon sx={{ color: theme.palette.primary.main }} />
            <Box>
              <Typography variant="h5" fontWeight={800} sx={{ lineHeight: 1.1 }}>
                AgentBlayna Calendar
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ display: { xs: "none", sm: "block" } }}
              >
                Light, calm, and paced — priority without chaos.
              </Typography>
            </Box>
          </Stack>

          <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end">
            <Button
              variant="outlined"
              startIcon={<TuneRoundedIcon />}
              onClick={() => setCallsPerDay((p) => (p === 3 ? 4 : p === 4 ? 2 : 3))}
              sx={{
                textTransform: "none",
                fontWeight: 900,
                borderRadius: 3,
                px: 1.5,
              }}
            >
              {callsPerDay}/day
            </Button>

            <Button
              variant="contained"
              startIcon={<AddRoundedIcon />}
              onClick={openAdd}
              sx={{
                borderRadius: 3,
                textTransform: "none",
                fontWeight: 950,
                px: 2,
                bgcolor: "#2563EB",
                "&:hover": { bgcolor: "#1D4ED8" },
              }}
            >
              Add
            </Button>
          </Stack>
        </Stack>
      </Card>

      {isMobile && (
        <MobileCalendarView
          model={model}
          alerts={alerts}
          manualEvents={manualEvents}
          loading={loading}
          onDayOpen={openDay}
        />
      )}

      {!isMobile && <Grid container spacing={2.5}>
        {/* Calendar */}
        <Grid item xs={12} md={8}>
          <Card sx={{ p: 2 }}>
            {/* Custom nav controls (modern) */}
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
              <Stack direction="row" spacing={1} alignItems="center">
                <IconButton
                  onClick={() => calendarRef.current?.getApi().prev()}
                  sx={navBtnSx}
                >
                  <ChevronLeftRoundedIcon />
                </IconButton>
                <IconButton
                  onClick={() => calendarRef.current?.getApi().next()}
                  sx={navBtnSx}
                >
                  <ChevronRightRoundedIcon />
                </IconButton>
                <Button
                  onClick={() => calendarRef.current?.getApi().today()}
                  sx={todayBtnSx}
                >
                  Today
                </Button>
              </Stack>

              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontWeight: 800 }}
              >
                {loading ? "Refreshing…" : " "}
              </Typography>
            </Stack>

            <FullCalendar
              ref={calendarRef}
              plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
              initialView="timeGridWeek"
              height={650}
              nowIndicator
              headerToolbar={{
                left: "",
                center: "title",
                right: "dayGridMonth,timeGridWeek",
              }}
              events={model.events}
              eventClick={(info) => {
                const kind = info.event.extendedProps?.kind;
                if (kind === "calls_group") {
                  openDay(info.event.extendedProps?.dateKey);
                } else {
                  const d = startOfDay(info.event.start);
                  openDay(dateKey(d));
                }
              }}
              eventContent={(arg) => {
                const kind = arg.event.extendedProps?.kind;

                if (kind === "calls_group") {
                  return (
                    <Box sx={bubbleSx(priorityColor)}>
                      <Box sx={dotSx(priorityColor.dot)} />
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography
                          sx={{
                            fontSize: 12.5,
                            fontWeight: 950,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          Priority Calls
                        </Typography>
                        <Typography
                          sx={{
                            mt: 0.15,
                            fontSize: 11,
                            fontWeight: 800,
                            opacity: 0.75,
                          }}
                        >
                          {arg.event.extendedProps?.calls?.length || 0} queued (no times)
                        </Typography>
                      </Box>
                    </Box>
                  );
                }

                const raw = arg.event.extendedProps?.raw;
                const manual = arg.event.extendedProps?.manual;

                const c =
                  kind === "deal"
                    ? dealColor
                    : kind === "personal"
                    ? personalColor
                    : kind === "manual"
                    ? neutralColor
                    : neutralColor;

                const title = manual?.title || raw?.summary || arg.event.title || "Task";

                return (
                  <Box sx={bubbleSx(c)}>
                    <Box sx={dotSx(c.dot)} />
                    <Typography
                      sx={{
                        fontSize: 12.2,
                        fontWeight: 900,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {title}
                    </Typography>
                  </Box>
                );
              }}
            />
          </Card>
        </Grid>

        {/* Right Rail */}
        <Grid item xs={12} md={4}>
          {/* AI Insights */}
          <Card>
            <Stack direction="row" spacing={1} alignItems="center" mb={1}>
              <AutoAwesomeRoundedIcon sx={{ color: "#2563EB" }} />
              <Typography fontWeight={950}>AI Insights</Typography>
              <Chip
                size="small"
                label="AgentBlayna"
                sx={{
                  ml: 0.5,
                  fontWeight: 950,
                  bgcolor: alpha("#2563EB", 0.10),
                  color: "#1D4ED8",
                }}
              />
            </Stack>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.25 }}>
              I paced your follow-ups to <strong>{callsPerDay} priority calls/day</strong>.
              Calls have no times — schedule them your way, or auto-fill later from uploaded data.
            </Typography>

            <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", mb: 1.25 }}>
              <Chip
                size="small"
                label={`${model.counts.callsQueued} calls queued`}
                sx={{
                  fontWeight: 900,
                  bgcolor: alpha("#FB7185", 0.16),
                  color: "#9F1239",
                }}
              />
              <Chip
                size="small"
                label={`${model.counts.upcomingEvents} upcoming events`}
                sx={{
                  fontWeight: 900,
                  bgcolor: alpha("#2563EB", 0.12),
                  color: "#1D4ED8",
                }}
              />
              <Chip
                size="small"
                label={`${model.counts.relationshipTouches} relationship touches`}
                sx={{
                  fontWeight: 900,
                  bgcolor: alpha("#7C3AED", 0.12),
                  color: "#5B21B6",
                }}
              />
            </Stack>

            <Divider sx={{ my: 1.25 }} />

            <Typography fontWeight={950} sx={{ mb: 0.9 }}>
              Top focus
            </Typography>

            <Stack spacing={0.8}>
              {(model.topFocus || []).map((a) => (
                <Box
                  key={a.id}
                  sx={{
                    p: 1.1,
                    borderRadius: 3,
                    border: `1px solid ${alpha(theme.palette.divider, 0.9)}`,
                    background: "linear-gradient(135deg, #fff 0%, #f8fafc 100%)",
                  }}
                >
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: 99,
                        bgcolor: isPriorityCall(a) ? priorityColor.dot : dealColor.dot,
                      }}
                    />
                    <Typography
                      sx={{
                        fontWeight: 950,
                        fontSize: 12.8,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {a.summary || a.type}
                    </Typography>
                  </Stack>
                </Box>
              ))}
            </Stack>

            <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
              <Button
                variant="outlined"
                onClick={loadAlerts}
                disabled={loading}
                sx={{ textTransform: "none", borderRadius: 3, fontWeight: 950 }}
              >
                {loading ? "Refreshing…" : "Refresh"}
              </Button>
              <Button
                variant="contained"
                onClick={() => setCallsPerDay(DEFAULT_CALLS_PER_DAY)}
                sx={{
                  textTransform: "none",
                  borderRadius: 3,
                  fontWeight: 950,
                  bgcolor: "#0F172A",
                  "&:hover": { bgcolor: "#111827" },
                }}
              >
                Reset pacing
              </Button>
            </Stack>
          </Card>

          {/* Next 7 days */}
          <Card sx={{ mt: 2.5 }}>
            <Typography fontWeight={950} sx={{ mb: 1 }}>
              Next 7 days
            </Typography>
            <Divider sx={{ mb: 1 }} />

            {!model.upcoming.length ? (
              <Typography variant="body2" color="text.secondary">
                Nothing scheduled — you’re clear.
              </Typography>
            ) : (
              <List dense sx={{ mx: -1 }}>
                {model.upcoming.slice(0, MAX_UPCOMING_RENDER).map((it, idx) => {
                  const dayLabel = it.day.toLocaleDateString(undefined, {
                    weekday: "short",
                    month: "short",
                    day: "numeric",
                  });

                  const title =
                    it.a?.summary ||
                    it.m?.title ||
                    "Task";

                  const subtitle =
                    it.type === "call"
                      ? "Priority call (no time)"
                      : it.a?.message || (it.type === "manual" ? "Manual" : "");

                  const dot =
                    it.type === "call"
                      ? priorityColor.dot
                      : it.a && (isBirthday(it.a) || isAnniversary(it.a))
                      ? personalColor.dot
                      : it.a && isDealEvent(it.a)
                      ? dealColor.dot
                      : neutralColor.dot;

                  return (
                    <ListItem
                      key={`${idx}_${title}`}
                      sx={{
                        px: 1,
                        py: 0.9,
                        borderRadius: 3,
                        mb: 0.8,
                        border: `1px solid ${alpha(theme.palette.divider, 0.9)}`,
                        background: "linear-gradient(135deg, #fff 0%, #f8fafc 100%)",
                      }}
                      secondaryAction={
                        it.a?.id ? (
                          <Button
                            size="small"
                            startIcon={<CheckCircleOutlineRoundedIcon />}
                            onClick={() => dismissAlert(it.a.id)}
                            sx={{ textTransform: "none", fontWeight: 950 }}
                          >
                            Done
                          </Button>
                        ) : null
                      }
                    >
                      <ListItemText
                        primary={
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Box
                              sx={{
                                width: 8,
                                height: 8,
                                borderRadius: 99,
                                bgcolor: dot,
                              }}
                            />
                            <Typography
                              sx={{
                                fontWeight: 950,
                                fontSize: 13,
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              }}
                            >
                              {title}
                            </Typography>

                            <Chip
                              size="small"
                              label={dayLabel}
                              sx={{
                                ml: "auto",
                                height: 22,
                                fontWeight: 950,
                                bgcolor: alpha("#2563EB", 0.08),
                                color: "#1D4ED8",
                              }}
                            />
                          </Stack>
                        }
                        secondary={
                          subtitle ? (
                            <Typography variant="caption" color="text.secondary">
                              {subtitle}
                            </Typography>
                          ) : null
                        }
                      />
                    </ListItem>
                  );
                })}
              </List>
            )}

            {model.upcoming.length > MAX_UPCOMING_RENDER ? (
              <Typography variant="caption" color="text.secondary">
                Showing top {MAX_UPCOMING_RENDER}. Open a day to see everything.
              </Typography>
            ) : null}
          </Card>
        </Grid>
      </Grid>}

      {/* Day detail modal */}
      <Dialog open={dayOpen} onClose={() => setDayOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 950 }}>
          {dayLabel}
          <IconButton
            onClick={() => setDayOpen(false)}
            sx={{ position: "absolute", right: 10, top: 10 }}
          >
            <CloseRoundedIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {!dayItems.length ? (
            <Typography variant="body2" color="text.secondary">
              Nothing scheduled.
            </Typography>
          ) : (
            <Stack spacing={1}>
              {dayItems.map((it, i) => {
                const title =
                  it.a?.summary ||
                  it.m?.title ||
                  it.a?.type ||
                  "Task";

                const isCall = it.kind === "call";
                const dot = isCall
                  ? priorityColor.dot
                  : it.a && (isBirthday(it.a) || isAnniversary(it.a))
                  ? personalColor.dot
                  : it.a && isDealEvent(it.a)
                  ? dealColor.dot
                  : neutralColor.dot;

                const message =
                  isCall
                    ? "Priority call (no time) — schedule it how you want."
                    : it.a?.message || (it.kind === "manual" ? "Manual item" : "");

                return (
                  <Box
                    key={`${i}_${title}`}
                    sx={{
                      p: 1.25,
                      borderRadius: 3,
                      border: `1px solid ${alpha(theme.palette.divider, 0.9)}`,
                      background: "linear-gradient(135deg, #fff 0%, #f8fafc 100%)",
                    }}
                  >
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Box sx={{ width: 8, height: 8, borderRadius: 99, bgcolor: dot }} />
                      <Typography fontWeight={950} sx={{ flex: 1 }}>
                        {title}
                      </Typography>

                      {it.a?.id ? (
                        <Button
                          size="small"
                          startIcon={<CheckCircleOutlineRoundedIcon />}
                          onClick={() => dismissAlert(it.a.id)}
                          sx={{ textTransform: "none", fontWeight: 950 }}
                        >
                          Done
                        </Button>
                      ) : null}
                    </Stack>

                    {message ? (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.6 }}>
                        {message}
                      </Typography>
                    ) : null}
                  </Box>
                );
              })}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              // quick add on this day
              // if dayLabel exists, derive key from items? easiest: keep last opened dayKey in state
              // v1: just open add with today if user wants more precision, they can pick date in dialog
              setAddDate(dateKey(new Date()));
              setAddOpen(true);
            }}
            startIcon={<AddRoundedIcon />}
            sx={{ textTransform: "none", fontWeight: 950 }}
          >
            Add
          </Button>
          <Button
            onClick={() => setDayOpen(false)}
            variant="contained"
            sx={{
              textTransform: "none",
              fontWeight: 950,
              borderRadius: 3,
              bgcolor: "#2563EB",
              "&:hover": { bgcolor: "#1D4ED8" },
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add modal */}
      <Dialog open={addOpen} onClose={() => setAddOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 950 }}>
          Add to calendar
          <IconButton
            onClick={() => setAddOpen(false)}
            sx={{ position: "absolute", right: 10, top: 10 }}
          >
            <CloseRoundedIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <ToggleButtonGroup
              value={addType}
              exclusive
              onChange={(_, v) => v && setAddType(v)}
              sx={{
                "& .MuiToggleButton-root": {
                  textTransform: "none",
                  fontWeight: 950,
                  borderRadius: 3,
                },
              }}
            >
              <ToggleButton value="task">Task</ToggleButton>
              <ToggleButton value="reminder">Reminder</ToggleButton>
              <ToggleButton value="event">Event</ToggleButton>
            </ToggleButtonGroup>

            <TextField
              label="Title"
              value={addTitle}
              onChange={(e) => setAddTitle(e.target.value)}
              placeholder="e.g. Prep flyers, Send reminder texts, Photography confirm…"
              fullWidth
            />

            <TextField
              label="Date"
              type="date"
              value={addDate}
              onChange={(e) => setAddDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />

            <Typography variant="caption" color="text.secondary">
              This creates a local calendar item (safe v1). We can persist these to the backend next.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddOpen(false)} sx={{ textTransform: "none", fontWeight: 950 }}>
            Cancel
          </Button>
          <Button
            onClick={createManualEvent}
            variant="contained"
            startIcon={<AddRoundedIcon />}
            sx={{
              textTransform: "none",
              fontWeight: 950,
              borderRadius: 3,
              bgcolor: "#2563EB",
              "&:hover": { bgcolor: "#1D4ED8" },
            }}
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

/* ------------------------------------------------------------------ */
/* Small style helpers */
/* ------------------------------------------------------------------ */

const navBtnSx = {
  bgcolor: "#f8fafc",
  border: "1px solid #e5e7eb",
  borderRadius: 3,
  boxShadow: "0 8px 22px rgba(15,23,42,0.07)",
  "&:hover": { bgcolor: "#eef2ff", borderColor: "#c7d2fe" },
};

const todayBtnSx = {
  textTransform: "none",
  fontWeight: 950,
  borderRadius: 3,
  px: 2,
  bgcolor: "#2563EB",
  color: "white",
  boxShadow: "0 12px 30px rgba(37,99,235,0.22)",
  "&:hover": { bgcolor: "#1D4ED8" },
};

const dotSx = (color) => ({
  width: 8,
  height: 8,
  borderRadius: 99,
  bgcolor: color,
  flex: "0 0 auto",
});
